import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Row, Col, Typography, Button, Space, message, Collapse, Input, List, Badge, Image, Cascader } from 'antd';
import { SaveOutlined, RollbackOutlined, LeftOutlined, RightOutlined, CopyOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import ReactJson from 'react-json-view';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import cardsData from '../../mock/cards.json';
import annotationsData from '../../mock/annotations.json';
import styleTagsData from '../../mock/styleTags.json'; // 导入风格标签数据

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

interface CardData {
  id: string;
  name: string;
  screenshot: string;
  dsl: any;
  status: string;
  datasets: string[];
  createTime: string;
  updateTime: string;
}

interface AnnotationData {
  cardId: string;
  layoutAnnotation: string;
  colorAnnotation: string;
  // 更新styleAnnotation为包含所有风格字段的对象
  styleAnnotation?: {
    colorStyle: string;
    layoutStyle: string;
    shapeStyle: string;
    visualStyle: string;
    decorationStyle: string;
    interactionStyle: string;
    scenarioStyle: string;
  };
  aiGeneratedLayout: string;
  aiGeneratedColor: string;
  createTime: string;
  updateTime: string;
}

interface ComponentCount {
  name: string;
  count: number;
}

const AnnotationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [messageApi, contextHolder] = message.useMessage();
  const [card, setCard] = useState<CardData | null>(null);
  const [annotation, setAnnotation] = useState<AnnotationData | null>(null);
  const [layoutAnnotation, setLayoutAnnotation] = useState('');
  const [colorAnnotation, setColorAnnotation] = useState('');
  // 初始化风格标注状态
  const [styleAnnotation, setStyleAnnotation] = useState<{
    colorStyle: string;
    layoutStyle: string;
    shapeStyle: string;
    visualStyle: string;
    decorationStyle: string;
    interactionStyle: string;
    scenarioStyle: string;
  }>({
    colorStyle: '亮色系',
    layoutStyle: '扁平化',
    shapeStyle: '圆角',
    visualStyle: '商务专业',
    decorationStyle: '插画风格',
    interactionStyle: '动效丰富',
    scenarioStyle: '电商风格'
  });
  const [showValidation, setShowValidation] = useState(false);
  const [leftWidth, setLeftWidth] = useState(230);
  const [middleWidth, setMiddleWidth] = useState(400);
  const [componentCounts, setComponentCounts] = useState<{
    layout: ComponentCount[];
    display: ComponentCount[];
    interaction: ComponentCount[];
  }>({
    layout: [],
    display: [],
    interaction: []
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(false);
  // 初始化AI面板宽度为50% (设置一个较大的初始值，实际宽度会在组件挂载后根据容器大小调整)
  const [aiPanelWidth, setAiPanelWidth] = useState(600);
  const [middleResizeHover, setMiddleResizeHover] = useState(false);
  const [aiPanelResizeHover, setAiPanelResizeHover] = useState(false);
  const [leftResizeHover, setLeftResizeHover] = useState(false);
  
  // 控制面板显示/隐藏的状态 (DSL默认隐藏，AI标注也默认隐藏)
  const [showDSLPanel, setShowDSLPanel] = useState(false);
  const [showAIAnnotations, setShowAIAnnotations] = useState(false); // 默认隐藏AI标注
  
  // 从Excel文件中读取的风格标签数据
  const styleOptions = styleTagsData.styleOptions;
  
  // refs for copy functionality
  const aiLayoutRef = useRef<HTMLDivElement>(null);
  const aiColorRef = useRef<HTMLDivElement>(null);
  
  // 修改useEffect中的处理逻辑
  useEffect(() => {
    // 加载卡片数据
    if (id) {
      const foundIndex = cardsData.cards.findIndex(card => card.id === id);
      setCurrentIndex(foundIndex);
      const foundCard = cardsData.cards[foundIndex];
      if (foundCard) {
        setCard(foundCard);
        
        // 计算组件数量
        countComponents(foundCard.dsl);
      }

      // 加载标注数据（如果存在）
      const foundAnnotation = annotationsData.annotations.find(anno => anno.cardId === id);
      if (foundAnnotation) {
        // 创建一个符合 AnnotationData 接口的新对象
        const annotationData: AnnotationData = {
          cardId: foundAnnotation.cardId,
          layoutAnnotation: foundAnnotation.layoutAnnotation,
          colorAnnotation: foundAnnotation.colorAnnotation,
          // 处理旧的styleAnnotation格式或设置默认值
          styleAnnotation: foundAnnotation.styleAnnotation && typeof foundAnnotation.styleAnnotation === 'object' && !Array.isArray(foundAnnotation.styleAnnotation)
            ? foundAnnotation.styleAnnotation
            : {
                colorStyle: '亮色系',
                layoutStyle: '扁平化',
                shapeStyle: '圆角',
                visualStyle: '商务专业',
                decorationStyle: '插画风格',
                interactionStyle: '动效丰富',
                scenarioStyle: '电商风格'
              },
          aiGeneratedLayout: foundAnnotation.aiGeneratedLayout,
          aiGeneratedColor: foundAnnotation.aiGeneratedColor,
          createTime: foundAnnotation.createTime,
          updateTime: foundAnnotation.updateTime
        };
        
        setAnnotation(annotationData);
        setLayoutAnnotation(foundAnnotation.layoutAnnotation);
        setColorAnnotation(foundAnnotation.colorAnnotation);
        // 设置风格标注状态
        if (annotationData.styleAnnotation) {
          setStyleAnnotation(annotationData.styleAnnotation);
        }
      } else {
        // 初始化新标注时显示AI辅助信息
        setAnnotation({
          cardId: id,
          layoutAnnotation: '',
          colorAnnotation: '',
          styleAnnotation: {
            colorStyle: '亮色系',
            layoutStyle: '扁平化',
            shapeStyle: '圆角',
            visualStyle: '商务专业',
            decorationStyle: '插画风格',
            interactionStyle: '动效丰富',
            scenarioStyle: '电商风格'
          },
          aiGeneratedLayout: '布局结构：采用 **垂直流式布局**，整体卡片分为五大功能模块，层次清晰、信息分层明确，遵循从上至下的阅读动线。各模块间通过默认间距（`gap-default`）与容器间距（`gap-container`）进行视觉分割，增强可读性与节奏感。 \n\n--- \n\n### 1. **顶部课程封面区（弹性布局001）** \n\n- **方向**：垂直布局（v） \n\n- **内容构成**： \n\n- **课程主图**：居中显示，尺寸为122×122px，四角圆角半径24px，呈现为圆形切角的封面图，展示课程主题图像。 \n\n- **主标题文本**：紧接图片下方，左对齐，字体加粗（600），字号24pt，行高28pt，内容为课程名称 `${object.Title}`。 \n\n- **副标题文本**：位于主标题下方，浅灰色文字（引用变量 `VariableID-180-514`），展示课程特色标签如“趣味实验 · 举一反三”。 \n\n> ✅ 视觉重点突出，图文结合强化品牌识别与吸引力。 \n\n--- \n\n### 2. **课程大纲展示区（弹性布局003）** \n\n- **方向**：垂直布局（v） \n\n- **内容构成**： \n\n- **标题栏**：“课程大纲”文字，加粗（600），字号20pt，行高24pt，深色主题文字。 \n\n- **三门课程科目横向排列区（弹性布局007）**： \n\n- **方向**：水平布局（h），子元素等间距分布（`halign: space-between`） \n\n- 每项包含： \n\n- 图标 + 文案组合（图标在前，文字在后） \n\n- 图标分别为： \n\n- 力学图标（remix-cursor-fill，颜色由变量控制） \n\n- 电磁学图标（remix-flashlight-fill，自定义亮色） \n\n- 光学图标（remix-loop-right-fill，自定义蓝绿色） \n\n- 文案内容分别为 `${object.Course_1}`、`${object.Course_2}`、`${object.Course_3}` \n\n> ✅ 水平图标导航式设计，直观传达课程核心模块，提升专业感与结构化印象。 \n\n--- \n\n### 3. **讲师信息区（弹性布局004）** \n\n- **方向**：水平布局（h） \n\n- **背景样式**：浅橙色背景（RGB: 239,246,255），四角圆角8px，内边距上下左右均为10px \n\n- **内部结构**： \n\n- **左侧头像**：圆形教师头像，尺寸40×40px，圆角半径99px（完全圆形） \n\n- **右侧信息列（弹性布局008）**： \n\n- 垂直布局（v） \n\n- 第一行：讲师姓名 `${object.TeacherName}`，加粗（600），字号16pt，行高18pt \n\n- 第二行（弹性布局009）：双行描述文本 \n\n- 上：教学资质 `${object.TeacherInfo_1}`（如“重点中学 | 高级物理教师”） \n\n- 下：教学经验 `${object.TeacherInfo_2}`（如“10年教学经验 · 名师讲义”），使用强调色（`VariableID-180-491`） \n\n> ✅ 强化师资信任背书，人设+资历双重输出，提升用户转化信心。 \n\n--- \n\n### 4. **价格与数据指标区（弹性布局005）** \n\n- **方向**：水平布局（h） \n\n- **两端对齐**：左侧价格信息，右侧报名&评分数据 \n\n- **左侧（弹性布局010）**： \n\n- 主价格：红色加粗字体（rgba(245,108,34,1)），格式“￥${object.Price}”，字号20pt，加粗600 \n\n- 标签：“限时优惠”提示语，使用辅助色（`VariableID-180-514`） \n\n- **右侧（弹性布局011）**： \n\n- 垂直排列两个指标： \n\n- “已有${object.Count}人报名” \n\n- “评分${object.Point}/5.0”，小字号（12pt），轻量级排版 \n\n> ✅ 利用社会认同（人数+评分）与价格锚点共同驱动决策，符合电商心理模型。 \n\n--- \n\n### 5. **操作按钮组（弹性布局006）** \n\n- **方向**：水平布局（h） \n\n- 包含两个权重均分按钮： \n\n- **按钮001：“加入购物车”** \n\n- 背景色：黄绿色（RGB: 51,180,178） \n\n- 白色文字（引用变量 `VariableID-214-381`），居中文案 \n\n- 四角大圆角（32px），营造柔和点击感 \n\n- **按钮002：“试听课程”** \n\n- 背景色：蓝色（RGB: 255,124,82） \n\n- 同样白色文字，相同样式规范 \n\n- 并列排布，形成对比选择 \n\n> ✅ 双行动按钮策略，满足不同用户阶段需求（立即购买 or 先体验），提升交互灵活性。 \n\n--- \n\n### 整体容器样式（ROOT） \n\n- **外边距**：默认间隙（`gap-default`） \n\n- **内边距**：四边均为默认内边距（`padding-default`） \n\n- **背景色**：默认背景色（`bg-default`） \n\n- **圆角**：统一使用默认圆角（`border-radius-default`），整体卡片呈现柔和矩形外观 \n\n- **对齐方式**：左对齐 + 顶部对齐，保证信息流清晰有序 \n\n--- \n\n✅ **总结设计语言特征**： \n\n- 层级分明：图像 → 标题 → 内容 → 讲师 → 数据 → 操作，符合用户认知路径 \n\n- 色彩系统：基于主题变量控制，支持动态换肤；关键信息使用高对比色突出 \n\n- 布局逻辑：以“垂直为主、水平为辅”，在关键模块引入横向布局打破单调 \n\n- 组件复用：所有文本、图片、按钮均使用标准化类型（如text1-bold/text4-regular等），便于统一维护 \n\n--- \n\n📌 **适用场景**：在线教育平台课程推荐卡片、知识付费商品展示、名师课程推广页等。',
          aiGeneratedColor: '颜色描述： \n\n**主题色**：蓝色 #1E88E5（用于“试听课程”按钮背景及边框） \n\n**辅助色**：绿色 #33B44E（用于“加入购物车”按钮背景及边框），橙色 #F56C22（用于价格文字“￥199”的主强调色） \n\n**中性色**： \n\n- 白色 #FFFFFF（未直接设定，作为默认文本与图标底色参考） \n\n- 浅灰底色 #EFF6FF（教师信息区域背景色，对应 RGB(239, 246, 255) → #EFF6FF） \n\n- 深灰文字 #333333（标题文本“高中物理进阶课程”，通过 fontSize=24 & fontWeight=600 强调，默认深色文本） \n\n- 中灰文字 #999999（副标题“趣味实验 · 举一反三”、教师简介第二行“10年教学经验 · 名师讲义”、评分“评分4.9/5.0”等次要信息，对应 VariableID-180-514 所指颜色，light 模式下为 rgba(153, 153, 153, 1) → #999999） \n\n- 浅灰文字 #BDBDBD（“已有1200人报名”文字颜色，VariableID-180-513，light 模式下为 rgba(189, 189, 189, 1) → #BDBDBD） \n\n**文本颜色应用分布**： \n\n- 主标题文本：“高中物理进阶课程” → 颜色继承 `text-color` 主题色，默认深灰 #333333 \n\n- 副标题文本：“趣味实验 · 举一反三” → 使用 VariableID-180-514 → #999999 \n\n- 课程大纲标题：“课程大纲” → 使用 `text-color` 主题色 → #333333 \n\n- 课程条目文本（力学与运动、电磁学、波动光学）→ 使用 `text-color` 主题色 → #333333 \n\n- 教师姓名：“李明老师” → 使用 `text-color` → #333333 \n\n- 教师信息第一行：“重点中学 | 高级物理教师” → 使用 `text-color` → #333333 \n\n- 教师信息第二行：“10年教学经验 · 名师讲义” → VariableID-180-491 → light 模式下为 RGB(245, 34, 219) → #F522DB（品红偏紫，用作标签强调） \n\n- 价格文本：“￥199” → 自定义 colorByMode 设置为 rgba(245, 108, 34, 1) → #F56C22（鲜明橙色，突出促销感） \n\n- 报名人数：“已有1200人报名” → VariableID-180-513 → #BDBDBD（浅灰色，弱化显示） \n\n- 评分文本：“评分4.9/5.0” → VariableID-180-514 → #999999 \n\n- 按钮文字：“加入购物车”、“试听课程” → 使用 VariableID-214-381，虽引用 themeColor token，但实际 light 模式下未覆盖，默认取白色 #FFFFFF（结合按钮背景推断需高对比度） \n\n**按钮颜色应用分布**： \n\n- “加入购物车”按钮： \n\n- 背景色：RGB(51, 180, 178) → #33B44E（青绿色） \n\n- 边框色：同背景 → #33B44E \n\n- 文字颜色：VariableID-214-381 → 推测为白色 #FFFFFF（确保可读性） \n\n- 圆角：32px（全圆角矩形） \n\n- “试听课程”按钮： \n\n- 背景色：RGB(82, 124, 255) → #527CFF（亮蓝色） \n\n- 边框色：同背景 → #527CFF \n\n- 文字颜色：同上 → #FFFFFF \n\n- 圆角：32px \n\n**分割线与装饰元素**： \n\n- 无显式分割线组件，但通过垂直间距（vgap）实现视觉分隔 \n\n- 图标颜色： \n\n- “remix-cursor-fill”（力学）→ VariableID-180-491 → #F522DB \n\n- “remix-flashlight-fill”（电磁学）→ 自定义 light 模式颜色 RGB(250, 219, 20) → #FADBB4（黄色系，发光意象） \n\n- “remix-loop-right-fill”（波动光学）→ 自定义 RGB(245, 34, 45) → #F5222D（红色系，循环动态） \n\n**背景色应用分布**： \n\n- 卡片整体背景：`bg-default` 主题背景色 → 默认浅灰白，推测为 #FFFFFF 或 #F5F7FA（未明确指定 RGB，按常规设计惯例） \n\n- 教师信息区域背景：RGB(239, 246, 255) → #EFF6FF（极浅蓝，轻微突出区块） \n\n- 内容容器圆角：整体卡片使用 `border-radius-default`，推测为 8px；图片使用 24px 和 99px（圆形头像） \n\n标准化输出如下： \n\n--- \n\n**颜色描述**： \n\n**主题色**：蓝色 #527CFF（用于“试听课程”按钮背景与边框） \n\n**辅助色**：青绿色 #33B44E（用于“加入购物车”按钮背景与边框），橙色 #F56C22（用于价格数字强调） \n\n**强调色**： \n\n- 品红色 #F522DB（用于“10年教学经验”标签类文本） \n\n- 黄色 #FADBB4（“电磁学”图标）、红色 #F5222D（“波动光学”图标）用于课程类别视觉区分 \n\n**中性色**： \n\n- 深灰文字 #333333（主标题、课程名称、教师姓名等主要文本） \n\n- 中灰文字 #999999（副标题、评分等次要信息） \n\n- 浅灰文字 #BDBDBD（“已报名人数”提示） \n\n- 背景色 #EFF6FF（教师信息区块底色） \n\n- 卡片底色 #FFFFFF（整体背景，默认 `bg-default`） \n\n**按钮文字色**：白色 #FFFFFF（两个按钮上的文字，保证与彩色背景的对比度） \n\n**图标色**：根据语义使用不同强调色（#F522DB / #FADBB4 / #F5222D）增强识别性 \n\n**圆角规范**： \n\n- 卡片容器：8px（`border-radius-default`） \n\n- 主图：24px（圆角图片） \n\n- 头像：99px（近似圆形） \n\n- 按钮：32px（胶囊型按钮） \n\n--- \n\n此结构符合标准化 UI 设计系统描述，涵盖主题、功能、状态与视觉层级。',
          createTime: new Date().toISOString(),
          updateTime: new Date().toISOString()
        });
      }
    }
    
    // 在组件挂载后设置AI面板宽度为容器的50%
    const handleResize = () => {
      // 获取右侧标注面板的宽度
      const rightPanel = document.querySelector('.right-annotation-panel');
      if (rightPanel) {
        const panelWidth = rightPanel.clientWidth;
        // 设置AI面板宽度为容器的50%，减去一些边距
        setAiPanelWidth(Math.max(panelWidth / 2 - 20, 300)); // 最小宽度300px
      }
    };
    
    // 初始设置
    setTimeout(handleResize, 100);
    
    // 监听窗口大小变化
    window.addEventListener('resize', handleResize);
    
    // 清理事件监听器
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [id]);

  // 计算组件数量
  const countComponents = (dsl: any) => {
    const layoutComponents: { [key: string]: number } = {
      'flex': 0,
      'list': 0,
      'grid': 0,
      'collapse': 0
    };
    
    const displayComponents: { [key: string]: number } = {
      'text': 0,
      'image': 0,
      'divider': 0,
      'icon': 0,
      'chart': 0,
      'audio': 0,
      'video': 0,
      'progress': 0
    };
    
    const interactionComponents: { [key: string]: number } = {
      'button': 0,
      'input': 0,
      'number-input': 0,
      'select': 0,
      'cascader': 0,
      'radio': 0,
      'checkbox': 0,
      'switch': 0,
      'datepicker': 0,
      'upload': 0
    };

    // 递归遍历DSL计算组件数量
    const traverse = (node: any) => {
      if (!node) return;
      
      const type = node.type?.toLowerCase();
      
      // 布局组件
      if (type === 'flex' || type === 'list' || type === 'grid' || type === 'collapse') {
        layoutComponents[type]++;
      }
      // 展示组件
      else if (type === 'text' || type === 'image' || type === 'divider' || 
               type === 'icon' || type === 'chart' || type === 'audio' || 
               type === 'video' || type === 'progress') {
        displayComponents[type]++;
      }
      // 交互组件
      else if (type === 'button' || type === 'input' || type === 'number-input' || 
               type === 'select' || type === 'cascader' || type === 'radio' || 
               type === 'checkbox' || type === 'switch' || type === 'datepicker' || 
               type === 'upload') {
        interactionComponents[type]++;
      }
      
      // 递归处理子节点
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach((child: any) => traverse(child));
      }
    };
    
    traverse(dsl);
    
    // 转换为组件列表
    setComponentCounts({
      layout: Object.entries(layoutComponents)
        .filter(([_, count]) => count > 0)
        .map(([name, count]) => ({ 
          name: mapComponentName(name, 'layout'), 
          count 
        })),
      display: Object.entries(displayComponents)
        .filter(([_, count]) => count > 0)
        .map(([name, count]) => ({ 
          name: mapComponentName(name, 'display'), 
          count 
        })),
      interaction: Object.entries(interactionComponents)
        .filter(([_, count]) => count > 0)
        .map(([name, count]) => ({ 
          name: mapComponentName(name, 'interaction'), 
          count 
        }))
    });
  };

  // 映射组件名称为中文
  const mapComponentName = (name: string, type: string): string => {
    const nameMap: { [key: string]: string } = {
      // 布局组件
      'flex': '弹性布局',
      'list': '列表',
      'grid': '网格',
      'collapse': '折叠面板',
      
      // 展示组件
      'text': '文本',
      'image': '图片',
      'divider': '分割线',
      'icon': '图标',
      'chart': '图表',
      'audio': '音频',
      'video': '视频',
      'progress': '进度条',
      
      // 交互组件
      'button': '按钮',
      'input': '文本输入框',
      'number-input': '数字输入框',
      'select': '下拉单选',
      'cascader': '级联选择',
      'radio': '单选列表',
      'checkbox': '多选列表',
      'switch': '开关',
      'datepicker': '时间选择',
      'upload': '文件上传'
    };
    
    return nameMap[name] || name;
  };

  // 修改handlePrevNext函数中的处理逻辑
  const handlePrevNext = (direction: number) => {
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < cardsData.cards.length) {
      const nextCard = cardsData.cards[newIndex];
      setCurrentIndex(newIndex);
      setCard(nextCard);
      
      // 加载标注数据（如果存在）
      const foundAnnotation = annotationsData.annotations.find(anno => anno.cardId === nextCard.id);
      if (foundAnnotation) {
        // 创建一个符合 AnnotationData 接口的新对象
        const annotationData: AnnotationData = {
          cardId: foundAnnotation.cardId,
          layoutAnnotation: foundAnnotation.layoutAnnotation,
          colorAnnotation: foundAnnotation.colorAnnotation,
          // 处理旧的styleAnnotation格式或设置默认值
          styleAnnotation: foundAnnotation.styleAnnotation && typeof foundAnnotation.styleAnnotation === 'object' && !Array.isArray(foundAnnotation.styleAnnotation)
            ? foundAnnotation.styleAnnotation
            : {
                colorStyle: '亮色系',
                layoutStyle: '扁平化',
                shapeStyle: '圆角',
                visualStyle: '商务专业',
                decorationStyle: '插画风格',
                interactionStyle: '动效丰富',
                scenarioStyle: '电商风格'
              },
          aiGeneratedLayout: foundAnnotation.aiGeneratedLayout,
          aiGeneratedColor: foundAnnotation.aiGeneratedColor,
          createTime: foundAnnotation.createTime,
          updateTime: foundAnnotation.updateTime
        };
        
        setAnnotation(annotationData);
        setLayoutAnnotation(foundAnnotation.layoutAnnotation);
        setColorAnnotation(foundAnnotation.colorAnnotation);
        // 设置风格标注状态
        if (annotationData.styleAnnotation) {
          setStyleAnnotation(annotationData.styleAnnotation);
        }
      } else {
        setAnnotation(null);
        setLayoutAnnotation('');
        setColorAnnotation('');
        // 重置风格标注状态为默认值
        setStyleAnnotation({
          colorStyle: '亮色系',
          layoutStyle: '扁平化',
          shapeStyle: '圆角',
          visualStyle: '商务专业',
          decorationStyle: '插画风格',
          interactionStyle: '动效丰富',
          scenarioStyle: '电商风格'
        });
      }
      
      countComponents(nextCard.dsl);
      setShowValidation(false);
    }
  };

  // 保存标注
  const handleSave = () => {
    if (!card) return;
    
    setShowValidation(true);
    
    // 只在校验失败时显示输入框下方的提示，不显示toast
    if (!layoutAnnotation || !layoutAnnotation.trim()) {
      return;
    }
    
    if (!colorAnnotation || !colorAnnotation.trim()) {
      return;
    }
    
    // 在实际应用中，这里应该调用API保存数据
    // 这里我们只是模拟保存成功
    messageApi.success('操作成功');
    
    // 更新卡片状态为已标注
    const updatedCard = { ...card, status: '已标注' };
    setCard(updatedCard);
    
    // 创建或更新标注数据
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    if (annotation) {
      const updatedAnnotation = {
        ...annotation,
        layoutAnnotation,
        colorAnnotation,
        styleAnnotation, // 保存风格标注
        updateTime: now
      };
      setAnnotation(updatedAnnotation as AnnotationData);
    } else {
      const newAnnotation: AnnotationData = {
        cardId: card.id,
        layoutAnnotation,
        colorAnnotation,
        styleAnnotation, // 保存风格标注
        aiGeneratedLayout: '垂直弹性布局，包含多个子组件',
        aiGeneratedColor: `主色：#333333
辅色：#666666
强调色：根据卡片类型有所不同`,
        createTime: now,
        updateTime: now
      };
      setAnnotation(newAnnotation);
    }
  };

  // 左侧面板大小调整
  const onResizeLeft = (_: React.SyntheticEvent, { size }: { size: { width: number; height: number } }) => {
    setLeftWidth(size.width);
  };

  // 中间面板大小调整
  const onResizeMiddle = (_: React.SyntheticEvent, { size }: { size: { width: number; height: number } }) => {
    setMiddleWidth(size.width);
  };

  // AI面板大小调整
  const onResizeAiPanel = (_: React.SyntheticEvent, { size }: { size: { width: number; height: number } }) => {
    setAiPanelWidth(size.width);
  };

  // 计算DSL的token数量（简化实现）
  const calculateTokenCount = (dsl: any): number => {
    // 这里是一个简化的token计算方法，实际项目中可能需要更复杂的逻辑
    return JSON.stringify(dsl).length;
  };

  if (!card) {
    return <div>加载中...</div>;
  }

  // 在return语句之前添加单选组选项数据
  // 色彩风格选项
  const colorStyleOptions = [
    { label: '亮色系', value: '亮色系' },
    { label: '暗色系', value: '暗色系' },
    { label: '高对比', value: '高对比' },
    { label: '柔和/马卡龙色', value: '柔和/马卡龙色' },
    { label: '渐变风格', value: '渐变风格' },
    { label: '单色/黑白灰', value: '单色/黑白灰' },
    { label: '品牌色主导', value: '品牌色主导' }
  ];

  // 排版与布局风格选项
  const layoutStyleOptions = [
    { label: '扁平化', value: '扁平化' },
    { label: '拟物化', value: '拟物化' },
    { label: '极简', value: '极简' },
    { label: '信息密集', value: '信息密集' },
    { label: '卡片化', value: '卡片化' },
    { label: '模块化', value: '模块化' },
    { label: '瀑布流', value: '瀑布流' },
    { label: '强网格对齐', value: '强网格对齐' }
  ];

  // 形状与深度选项
  const shapeStyleOptions = [
    { label: '圆角', value: '圆角' },
    { label: '直角', value: '直角' },
    { label: '有机/不规则形状', value: '有机/不规则形状' },
    { label: '重阴影/立体感', value: '重阴影/立体感' },
    { label: '轻阴影', value: '轻阴影' },
    { label: '无阴影/扁平', value: '无阴影/扁平' }
  ];

  // 视觉氛围选项
  const visualStyleOptions = [
    { label: '商务专业', value: '商务专业' },
    { label: '活泼年轻', value: '活泼年轻' },
    { label: '科技未来感', value: '科技未来感' },
    { label: '温暖亲和', value: '温暖亲和' },
    { label: '高级极简', value: '高级极简' },
    { label: '趣味/卡通', value: '趣味/卡通' }
  ];

  // 装饰元素选项
  const decorationStyleOptions = [
    { label: '插画风格', value: '插画风格' },
    { label: '图标主导', value: '图标主导' },
    { label: '写实照片', value: '写实照片' },
    { label: '线稿风格', value: '线稿风格' },
    { label: '质感/纹理', value: '质感/纹理' },
    { label: '无装饰', value: '无装饰' }
  ];

  // 交互反馈风格选项
  const interactionStyleOptions = [
    { label: '动效丰富', value: '动效丰富' },
    { label: '轻微过渡', value: '轻微过渡' },
    { label: '无动效', value: '无动效' },
    { label: '悬浮反馈', value: '悬浮反馈' },
    { label: '按压反馈', value: '按压反馈' }
  ];

  // 适配场景风格选项
  const scenarioStyleOptions = [
    { label: '电商风格', value: '电商风格' },
    { label: '工具型', value: '工具型' },
    { label: '社交活跃', value: '社交活跃' },
    { label: '金融可信', value: '金融可信' },
    { label: '医疗洁净', value: '医疗洁净' },
    { label: '教育结构化', value: '教育结构化' },
    { label: '娱乐沉浸', value: '娱乐沉浸' }
  ];

  // 复制AI布局标注到人工标注
  const copyAILayoutToManual = () => {
    if (annotation?.aiGeneratedLayout) {
      // 去掉代码格式，只保留纯文本
      let plainText = annotation.aiGeneratedLayout;
      
      // 去掉标题标记
      plainText = plainText.replace(/^#+\s*/gm, '');
      
      // 去掉粗体和斜体标记
      plainText = plainText.replace(/\*\*(.*?)\*\*/g, '$1');
      plainText = plainText.replace(/\*(.*?)\*/g, '$1');
      plainText = plainText.replace(/__(.*?)__/g, '$1');
      plainText = plainText.replace(/_(.*?)_/g, '$1');
      
      // 去掉代码标记
      plainText = plainText.replace(/`{3}.*?\n([\s\S]*?)`{3}/g, '$1');
      plainText = plainText.replace(/`(.*?)`/g, '$1');
      
      // 去掉链接标记
      plainText = plainText.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1');
      
      // 去掉引用标记
      plainText = plainText.replace(/^>\s*/gm, '');
      
      // 去掉分割线
      plainText = plainText.replace(/^\s*[-*_]{3,}\s*$/gm, '');
      
      // 去掉列表标记（只去掉 * + - 开头的列表标记，保留数字列表）
      plainText = plainText.replace(/^[\s]*[\*\+\-]\s*/gm, '');
      
      // 合并多个换行符为两个换行符
      plainText = plainText.replace(/\n{3,}/g, '\n\n');
      
      // 去掉行首行尾空格
      plainText = plainText.trim();
      
      setLayoutAnnotation(plainText);
      messageApi.success('已复制AI标注到人工标注');
    }
  };

  // 复制AI配色标注到人工标注
  const copyAIColorToManual = () => {
    if (annotation?.aiGeneratedColor) {
      // 去掉代码格式，只保留纯文本
      let plainText = annotation.aiGeneratedColor;
      
      // 去掉标题标记
      plainText = plainText.replace(/^#+\s*/gm, '');
      
      // 去掉粗体和斜体标记
      plainText = plainText.replace(/\*\*(.*?)\*\*/g, '$1');
      plainText = plainText.replace(/\*(.*?)\*/g, '$1');
      plainText = plainText.replace(/__(.*?)__/g, '$1');
      plainText = plainText.replace(/_(.*?)_/g, '$1');
      
      // 去掉代码标记
      plainText = plainText.replace(/`{3}.*?\n([\s\S]*?)`{3}/g, '$1');
      plainText = plainText.replace(/`(.*?)`/g, '$1');
      
      // 去掉链接标记
      plainText = plainText.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1');
      
      // 去掉引用标记
      plainText = plainText.replace(/^>\s*/gm, '');
      
      // 去掉分割线
      plainText = plainText.replace(/^\s*[-*_]{3,}\s*$/gm, '');
      
      // 去掉列表标记（只去掉 * + - 开头的列表标记，保留数字列表）
      plainText = plainText.replace(/^[\s]*[\*\+\-]\s*/gm, '');
      
      // 合并多个换行符为两个换行符
      plainText = plainText.replace(/\n{3,}/g, '\n\n');
      
      // 去掉行首行尾空格
      plainText = plainText.trim();
      
      setColorAnnotation(plainText);
      // messageApi.success('已复制AI配色标注到人工标注');
    }
  };

  // 复制人工布局标注
  const copyManualLayout = () => {
    if (layoutAnnotation) {
      navigator.clipboard.writeText(layoutAnnotation);
      messageApi.success('已复制到剪贴板');
    }
  };

  // 复制人工配色标注
  const copyManualColor = () => {
    if (colorAnnotation) {
      navigator.clipboard.writeText(colorAnnotation);
      messageApi.success('已复制到剪贴板');
    }
  };

  return (
    <div style={{ 
      height: '100vh',  // 高度自适应
      display: 'flex', 
      flexDirection: 'column',
      padding: '24px'  // 保留原有的边距
    }}>
      {contextHolder}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Title level={4} style={{ margin: 0, color: '#1890ff' }}>{card?.name}</Title>
            {card && (
              <Badge 
                status={card.status === '已标注' ? 'success' : 'default'} 
                text={card.status} 
              />
            )}
          </div>
          <Space>
            <Button 
              icon={showDSLPanel ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => setShowDSLPanel(!showDSLPanel)}
              size="small"
            >
              {showDSLPanel ? '隐藏DSL' : '显示DSL'}
            </Button>
            <Button 
              icon={showAIAnnotations ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => setShowAIAnnotations(!showAIAnnotations)}
              size="small"
            >
              {showAIAnnotations ? '隐藏AI标注' : '显示AI标注'}
            </Button>
          </Space>
        </div>
        
        <Space>
          <Button 
            icon={<LeftOutlined />} 
            onClick={() => handlePrevNext(-1)}
            disabled={currentIndex === 0}
          >
            上一条
          </Button>
          <Text>
            {currentIndex + 1}/{cardsData.cards.length}
          </Text>
          <Button 
            icon={<RightOutlined />} 
            onClick={() => handlePrevNext(1)}
            disabled={currentIndex === cardsData.cards.length - 1}
          >
            下一条
          </Button>
        </Space>
        
        <Space>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>保存标注</Button>
        </Space>
      </div>
      
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* 左侧DSL面板 */}
        {showDSLPanel && leftWidth > 0 && (
          <Resizable
            width={leftWidth}
            height={0}
            handle={<div 
              className="custom-handle" 
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: leftResizeHover ? '3px' : '5px',
                cursor: 'col-resize',
                backgroundColor: leftResizeHover ? '#1890ff' : 'transparent',
                zIndex: 1
              }}
              onMouseEnter={() => setLeftResizeHover(true)}
              onMouseLeave={() => setLeftResizeHover(false)}
            />}
            onResize={onResizeLeft}
            axis="x"
          >
          <div style={{
            width: leftWidth,
            height: '100%',
            overflow: 'auto',
            padding: '16px',
            border: '1px solid #f0f0f0',
            borderRadius: '4px',
            position: 'relative'
          }}>
            <Title level={5}>卡片DSL (token总数: {card ? calculateTokenCount(card.dsl) : 0})</Title>
            <div style={{
              backgroundColor: '#1e1e1e',
              padding: '12px',
              borderRadius: '4px',
              maxHeight: 'calc(100% - 120px)',
              overflow: 'auto'
            }}>
              <ReactJson
                src={card?.dsl}
                theme="monokai"
                displayDataTypes={false}
                collapsed={1}  // 默认只展开第一层
                collapseStringsAfterLength={80}
                style={{ backgroundColor: 'transparent' }}
                enableClipboard={true}
                displayObjectSize={false}
                iconStyle="triangle"
                shouldCollapse={(field) => field.name === 'root'}  // 只对根节点应用收起逻辑
                groupArraysAfterLength={100}
                onSelect={(select) => console.log('selected:', select)}
              />
            </div>
            <div style={{ marginTop: '16px' }}>
              <Title level={5} style={{ marginBottom: '16px' }}>组件信息</Title>
              <Collapse defaultActiveKey={['1', '2', '3']}>
                {componentCounts.layout.length > 0 && (
                  <Panel header="布局组件" key="1">
                    <List
                      size="small"
                      dataSource={componentCounts.layout}
                      renderItem={item => (
                        <List.Item>
                          <Text>{item.name}</Text>
                          <Text strong>{item.count}</Text>
                        </List.Item>
                      )}
                    />
                  </Panel>
                )}
                {componentCounts.display.length > 0 && (
                  <Panel header="展示组件" key="2">
                    <List
                      size="small"
                      dataSource={componentCounts.display}
                      renderItem={item => (
                        <List.Item>
                          <Text>{item.name}</Text>
                          <Text strong>{item.count}</Text>
                        </List.Item>
                      )}
                    />
                  </Panel>
                )}
                {componentCounts.interaction.length > 0 && (
                  <Panel header="交互组件" key="3">
                    <List
                      size="small"
                      dataSource={componentCounts.interaction}
                      renderItem={item => (
                        <List.Item>
                          <Text>{item.name}</Text>
                          <Text strong>{item.count}</Text>
                        </List.Item>
                      )}
                    />
                  </Panel>
                )}
              </Collapse>
            </div>
          </div>
          </Resizable>
        )}
        
        {/* 中间图片预览面板 */}
        {middleWidth > 0 && (
          <Resizable
            width={middleWidth}
            height={0}
            handle={<div 
              className="custom-handle" 
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: middleResizeHover ? '3px' : '5px',
                cursor: 'col-resize',
                backgroundColor: middleResizeHover ? '#1890ff' : 'transparent',
                zIndex: 1
              }}
              onMouseEnter={() => setMiddleResizeHover(true)}
              onMouseLeave={() => setMiddleResizeHover(false)}
            />}
            onResize={onResizeMiddle}
            axis="x"
          >
          <div style={{ 
            width: middleWidth, 
            height: '100%', 
            overflow: 'auto',
            padding: '16px',
            border: '1px solid #f0f0f0',
            borderRadius: '4px',
            marginLeft: showDSLPanel ? '8px' : '0',  // 当DSL面板隐藏时，移除左边距
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <Title level={5}>卡片预览</Title>
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              overflow: 'hidden'
            }}>
              <img 
                src={card?.screenshot} 
                alt={card?.name}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%',
                  objectFit: 'contain',
                  cursor: 'pointer'
                }}
                onClick={() => setPreviewVisible(true)}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://mk-prod-1251517970.cos.ap-guangzhou.myqcloud.com/415924262275928064/agent/agent/card/wx/Overlay%2BBorder%2BShadow%2BOverlayBlur.png';
                }}
              />
              <Image
                width={0}
                height={0}
                style={{ display: 'none' }}
                src={card?.screenshot}
                preview={{
                  visible: previewVisible,
                  onVisibleChange: (visible: boolean) => setPreviewVisible(visible),
                }}
              />
            </div>
          </div>
          </Resizable>
        )}
        
        {/* 右侧标注面板 */}
        <div className="right-annotation-panel" style={{ 
          flex: 1, 
          height: '100%', 
          overflow: 'auto',
          padding: '16px',
          border: '1px solid #f0f0f0',
          borderRadius: '4px',
          marginLeft: '8px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Title level={5} style={{ margin: 0 }}>卡片标注</Title>
            {/* 将复制AI标注到人工标注按钮移动到卡片标注行 */}
            <Button 
              icon={<CopyOutlined />}
              onClick={() => {
                copyAILayoutToManual();
                copyAIColorToManual();
              }}
              size="small"
            >
              复制AI标注到人工标注
            </Button>
          </div>
          
          <div style={{ display: 'flex', height: 'calc(100% - 40px)' }}>
            {/* AI辅助标注面板 */}
            {showAIAnnotations && (
              <Resizable
                width={aiPanelWidth}
                height={0}
                handle={<div 
                  className="custom-handle" 
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: aiPanelResizeHover ? '3px' : '1px',
                    cursor: 'col-resize',
                    backgroundColor: aiPanelResizeHover ? '#1890ff' : '#f0f0f0',
                    zIndex: 1,
                    boxShadow: '2px 0 4px -2px rgba(0,0,0,0.2)'
                  }}
                  onMouseEnter={() => setAiPanelResizeHover(true)}
                  onMouseLeave={() => setAiPanelResizeHover(false)}
                />}
                onResize={onResizeAiPanel}
                axis="x"
              >
                <div style={{ 
                  width: aiPanelWidth, 
                  height: '100%', 
                  overflow: 'auto',
                  paddingRight: '16px'
                }}>
                  <Collapse defaultActiveKey={['layout', 'color', 'thinking']} ghost>
                    <Panel 
                      header={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>AI布局标注</span>
                          {/* 移除复制按钮 */}
                        </div>
                      } 
                      key="layout"
                    >
                      <div 
                        ref={aiLayoutRef}
                        style={{ 
                          backgroundColor: '#f9f9f9', 
                          padding: '8px', 
                          borderRadius: '4px',
                          whiteSpace: 'pre-line'
                        }}
                      >
                        {annotation?.aiGeneratedLayout || '暂无AI布局标注'}
                      </div>
                    </Panel>
                    
                    <Panel 
                      header={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>AI配色标注</span>
                          {/* 移除复制按钮 */}
                        </div>
                      } 
                      key="color"
                    >
                      <div 
                        ref={aiColorRef}
                        style={{ 
                          backgroundColor: '#f9f9f9', 
                          padding: '8px', 
                          borderRadius: '4px',
                          whiteSpace: 'pre-line'
                        }}
                      >
                        {annotation?.aiGeneratedColor || '暂无AI配色标注'}
                      </div>
                    </Panel>
                    <Panel header="AI思考过程" key="thinking">
                      <div style={{ 
                        backgroundColor: '#f9f9f9', 
                        padding: '8px', 
                        borderRadius: '4px',
                        whiteSpace: 'pre-line'
                      }}>
                        {annotation?.aiGeneratedColor 
                          ? `基于卡片内容分析，我建议采用以下配色方案：
1. 主色调：根据卡片类型确定主要颜色
2. 辅助色：用于次要元素和背景
3. 强调色：用于重要操作和交互元素
4. 文字颜色：确保良好的可读性
5. 背景色：保持整体视觉平衡`
                          : '暂无AI思考过程'}
                      </div>
                    </Panel>
                  </Collapse>
                </div>
              </Resizable>
            )}
            
            {/* 人工标注面板 */}
            <div style={{ flex: 1, height: '100%', overflow: 'auto', paddingLeft: showAIAnnotations ? '16px' : '0' }}>
              <Collapse defaultActiveKey={['1', '2', '3']} ghost>
                <Panel 
                  header={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>人工布局标注</span>
                      <Button 
                        icon={<CopyOutlined />} 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          copyManualLayout();
                        }}
                      >
                        复制
                      </Button>
                    </div>
                  } 
                  key="1"
                >
                  <div style={{ position: 'relative' }}>
                    <TextArea
                      value={layoutAnnotation}
                      onChange={e => setLayoutAnnotation(e.target.value)}
                      placeholder="请输入布局标注内容..."
                      autoSize={{ minRows: 4 }}
                      style={{ 
                        marginTop: '8px',
                        resize: 'vertical',
                        minHeight: '120px'
                      }}
                    />
                    <Text 
                      type="secondary" 
                      style={{ 
                        position: 'absolute',
                        right: 8,
                        bottom: 8,
                        fontSize: 12,
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        padding: '0 4px',
                        borderRadius: 4
                      }}
                    >
                      {layoutAnnotation.length} 字符
                    </Text>
                  </div>
                  {showValidation && (!layoutAnnotation || !layoutAnnotation.trim()) && (
                    <Text type="danger" style={{ fontSize: 12, marginTop: 4 }}>
                      请输入标注信息
                    </Text>
                  )}
                </Panel>
                
                <Panel 
                  header={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>人工配色标注</span>
                      <Button 
                        icon={<CopyOutlined />} 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          copyManualColor();
                        }}
                      >
                        复制
                      </Button>
                    </div>
                  } 
                  key="2"
                >
                  <div style={{ position: 'relative' }}>
                    <TextArea
                      value={colorAnnotation}
                      onChange={e => setColorAnnotation(e.target.value)}
                      placeholder="请输入配色标注内容..."
                      autoSize={{ minRows: 4 }}
                      style={{ 
                        marginTop: '8px',
                        resize: 'vertical',
                        minHeight: '120px'
                      }}
                    />
                    <Text 
                      type="secondary" 
                      style={{ 
                        position: 'absolute',
                        right: 8,
                        bottom: 8,
                        fontSize: 12,
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        padding: '0 4px',
                        borderRadius: 4
                      }}
                    >
                      {colorAnnotation.length} 字符
                    </Text>
                  </div>
                  {showValidation && (!colorAnnotation || !colorAnnotation.trim()) && (
                    <Text type="danger" style={{ fontSize: 12, marginTop: 4 }}>
                      请输入标注信息
                    </Text>
                  )}
                </Panel>
                
                {/* 修改卡片风格标注面板 */}
                <Panel header="卡片风格标注" key="3">
                  <div style={{ marginTop: '8px' }}>
                    {/* 色彩风格 */}
                    <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start' }}>
                      <Text strong style={{ marginTop: '4px', minWidth: '120px', textAlign: 'right' }}>色彩风格：</Text>
                      <div style={{ 
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '16px',
                        marginLeft: '8px'
                      }}>
                        {colorStyleOptions.map(option => (
                          <label key={option.value} style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            whiteSpace: 'nowrap'
                          }}>
                            <input
                              type="radio"
                              name="colorStyle"
                              value={option.value}
                              checked={styleAnnotation.colorStyle === option.value}
                              onChange={(e) => setStyleAnnotation({...styleAnnotation, colorStyle: e.target.value})}
                              style={{ marginRight: '4px' }}
                            />
                            {option.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* 排版与布局风格 */}
                    <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start' }}>
                      <Text strong style={{ marginTop: '4px', minWidth: '120px', textAlign: 'right' }}>排版与布局风格：</Text>
                      <div style={{ 
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '16px',
                        marginLeft: '8px'
                      }}>
                        {layoutStyleOptions.map(option => (
                          <label key={option.value} style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            whiteSpace: 'nowrap'
                          }}>
                            <input
                              type="radio"
                              name="layoutStyle"
                              value={option.value}
                              checked={styleAnnotation.layoutStyle === option.value}
                              onChange={(e) => setStyleAnnotation({...styleAnnotation, layoutStyle: e.target.value})}
                              style={{ marginRight: '4px' }}
                            />
                            {option.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* 形状与深度 */}
                    <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start' }}>
                      <Text strong style={{ marginTop: '4px', minWidth: '120px', textAlign: 'right' }}>形状与深度：</Text>
                      <div style={{ 
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '16px',
                        marginLeft: '8px'
                      }}>
                        {shapeStyleOptions.map(option => (
                          <label key={option.value} style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            whiteSpace: 'nowrap'
                          }}>
                            <input
                              type="radio"
                              name="shapeStyle"
                              value={option.value}
                              checked={styleAnnotation.shapeStyle === option.value}
                              onChange={(e) => setStyleAnnotation({...styleAnnotation, shapeStyle: e.target.value})}
                              style={{ marginRight: '4px' }}
                            />
                            {option.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* 视觉氛围 */}
                    <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start' }}>
                      <Text strong style={{ marginTop: '4px', minWidth: '120px', textAlign: 'right' }}>视觉氛围：</Text>
                      <div style={{ 
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '16px',
                        marginLeft: '8px'
                      }}>
                        {visualStyleOptions.map(option => (
                          <label key={option.value} style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            whiteSpace: 'nowrap'
                          }}>
                            <input
                              type="radio"
                              name="visualStyle"
                              value={option.value}
                              checked={styleAnnotation.visualStyle === option.value}
                              onChange={(e) => setStyleAnnotation({...styleAnnotation, visualStyle: e.target.value})}
                              style={{ marginRight: '4px' }}
                            />
                            {option.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* 装饰元素 */}
                    <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start' }}>
                      <Text strong style={{ marginTop: '4px', minWidth: '120px', textAlign: 'right' }}>装饰元素：</Text>
                      <div style={{ 
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '16px',
                        marginLeft: '8px'
                      }}>
                        {decorationStyleOptions.map(option => (
                          <label key={option.value} style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            whiteSpace: 'nowrap'
                          }}>
                            <input
                              type="radio"
                              name="decorationStyle"
                              value={option.value}
                              checked={styleAnnotation.decorationStyle === option.value}
                              onChange={(e) => setStyleAnnotation({...styleAnnotation, decorationStyle: e.target.value})}
                              style={{ marginRight: '4px' }}
                            />
                            {option.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* 交互反馈风格 */}
                    <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start' }}>
                      <Text strong style={{ marginTop: '4px', minWidth: '120px', textAlign: 'right' }}>交互反馈风格：</Text>
                      <div style={{ 
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '16px',
                        marginLeft: '8px'
                      }}>
                        {interactionStyleOptions.map(option => (
                          <label key={option.value} style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            whiteSpace: 'nowrap'
                          }}>
                            <input
                              type="radio"
                              name="interactionStyle"
                              value={option.value}
                              checked={styleAnnotation.interactionStyle === option.value}
                              onChange={(e) => setStyleAnnotation({...styleAnnotation, interactionStyle: e.target.value})}
                              style={{ marginRight: '4px' }}
                            />
                            {option.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* 适配场景风格 */}
                    <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start' }}>
                      <Text strong style={{ marginTop: '4px', minWidth: '120px', textAlign: 'right' }}>适配场景风格：</Text>
                      <div style={{ 
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '16px',
                        marginLeft: '8px'
                      }}>
                        {scenarioStyleOptions.map(option => (
                          <label key={option.value} style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            whiteSpace: 'nowrap'
                          }}>
                            <input
                              type="radio"
                              name="scenarioStyle"
                              value={option.value}
                              checked={styleAnnotation.scenarioStyle === option.value}
                              onChange={(e) => setStyleAnnotation({...styleAnnotation, scenarioStyle: e.target.value})}
                              style={{ marginRight: '4px' }}
                            />
                            {option.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </Panel>
              </Collapse>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnotationDetail;